import browserFingerprint from './utils/imprint'
import { SquareCardTokenizationError, SquarePaymentError, SquarePaymentFailure, SquareServiceError } from '../../components/square/errors'
import isNumber from 'lodash/isNumber'
import testDeviceCodes from './testMode-DeviceCodes.js'

const filterHeaders = (h) => Object.entries(h).reduce((acc, [k, v]) => v ? { ...acc, [k]: v } : acc, {})

// Helper function to generate UUID v4
const generateUUID = () => {
  return 'xxxxxxxx-xxxx-4xxx-yxxx-xxxxxxxxxxxx'.replace(/[xy]/g, function(c) {
    const r = Math.random() * 16 | 0
    const v = c === 'x' ? r : (r & 0x3 | 0x8)
    return v.toString(16)
  })
}

// Helper function to map Square API location to internal form fields
const mapLocationFromAPI = (location) => {
  const mapped = {
    id: location.id,
    name: location.name,
    description: location.description || '',
    type: location.type || 'PHYSICAL',
    status: location.status,
    twitter_username: location.twitter_username || '',
    instagram_username: location.instagram_username || '',
    facebook_url: location.facebook_url || '',

    // Map address fields
    address: {
      address_line_1: location.address?.address_line_1 || '',
      address_line_2: location.address?.address_line_2 || '',
      locality: location.address?.locality || '',
      administrative_district_level_1: location.address?.administrative_district_level_1 || '',
      postal_code: location.address?.postal_code || '',
      country: location.address?.country || ''
    },

    // Map business hours
    business_hours: []
  }

  // Process business hours from API format to internal format
  if (location.business_hours && location.business_hours.periods && Array.isArray(location.business_hours.periods)) {
    // Group business hours by same open/close times to match UI behavior
    const timeGroups = {}

    location.business_hours.periods
      .filter(period => period && period.day_of_week) // Filter out invalid periods
      .forEach(period => {
        const openTime = period.start_local_time ? period.start_local_time.substring(0, 5) : '' // Convert "10:00:00" to "10:00"
        const closeTime = period.end_local_time ? period.end_local_time.substring(0, 5) : '' // Convert "18:00:00" to "18:00"
        const timeKey = `${openTime}-${closeTime}`

        if (!timeGroups[timeKey]) {
          timeGroups[timeKey] = {
            openTime,
            closeTime,
            days: [],
            uuid: generateUUID()
          }
        }

        timeGroups[timeKey].days.push(period.day_of_week)
      })

    // Convert grouped business hours to flat array format expected by components
    mapped.business_hours = Object.values(timeGroups).flatMap(group =>
      group.days.map(day => ({
        dayOfWeek: day,
        openTime: group.openTime,
        closeTime: group.closeTime,
        uuid: group.uuid
      }))
    )
  }

  return mapped
}

// Helper function to map internal form fields to Square API format
const mapLocationToAPI = (formData) => {
  // Process business hours from internal format to API format
  const processedBusinessHours = (formData.business_hours || []).map(hour => ({
    day_of_week: hour.dayOfWeek,
    start_local_time: hour.openTime ? `${hour.openTime}:00` : '', // Convert "10:00" to "10:00:00"
    end_local_time: hour.closeTime ? `${hour.closeTime}:00` : '' // Convert "18:00" to "18:00:00"
    // UUID is excluded intentionally as it's only for internal use
  }))

  return {
    name: formData.name,
    description: formData.description || formData.name, // Use name as description if not provided
    address: {
      address_line_1: formData.address_line_1,
      address_line_2: formData.address_line_2 || '',
      locality: formData.city,
      administrative_district_level_1: formData.state,
      postal_code: formData.postal_code
    },
    twitter_username: formData.twitter_username || null,
    instagram_username: formData.instagram_username || null,
    facebook_url: formData.facebook_url || null,
    type: formData.type || 'PHYSICAL',
    business_hours: {
      periods: processedBusinessHours
    }
  }
}

let getIsConnectedPromise = null

// The payment service may wrap a payload in a standard response envelope
// ({ status, timestamp, date, data: {...} }). Unwrap to the inner data only
// when it clearly looks like an envelope, so a genuine map isn't mangled.
const unwrapEnvelope = (obj) => {
  if (obj && typeof obj === 'object' && !Array.isArray(obj) &&
      obj.data && typeof obj.data === 'object' &&
      ('status' in obj || 'timestamp' in obj || 'date' in obj)) {
    return obj.data
  }
  return obj
}

// Normalise the available transaction types to a flat { <txntype>: { label } }
// map regardless of the source shape (object map, array of objects, or string
// labels), after unwrapping any response envelope.
const normalizeTransactionTypes = (raw) => {
  const src = unwrapEnvelope(raw)
  const out = {}
  if (!src || typeof src !== 'object') {
    return out
  }
  if (Array.isArray(src)) {
    src.forEach((item) => {
      if (!item || typeof item !== 'object') return
      const key = item.txntype || item.key || item.id || item.name
      if (!key) return
      out[key] = { label: item.label || item.title || item.name || key }
    })
    return out
  }
  Object.keys(src).forEach((key) => {
    const v = src[key]
    if (typeof v === 'string') {
      out[key] = { label: v }
    }
    else if (v && typeof v === 'object') {
      out[key] = { label: v.label || v.title || v.name || key }
    }
    else {
      out[key] = { label: key }
    }
  })
  return out
}

export default {
  init: function({ commit }, payload) {
    commit('SET_MODE', payload.mode)
    commit('SET_ENDPOINT', payload.endpoint)
  },
  oauth2Connect: function({ dispatch }, payload) {
    const expiresTime = new Date()
    expiresTime.setTime(expiresTime.getTime() + (5 * 60 * 1000)) // 5 Minutes
    return dispatch('paymentAPICall', {
      method: 'POST',
      url: '/auth/connect',
      body: {
        currency: payload.currency,
        redirect: encodeURI(`${window.location.protocol}//${window.location.host}${payload.redirect}`)
      }
    })
      .then(resp => {
        if (resp.status) {
          return resp.url
        }
        else {
          throw new SquareServiceError('OAuth2 Connect Error', { code: resp.code, reason: resp.reason, category: resp.category })
        }
      })
  },
  paymentAPICall: function({ state, rootGetters, commit }, payload) {
    const endpoint = state.endpoint.replace(new RegExp('/+$'), '') + '/square/v1'
    const url = `${endpoint}${payload.url}`
    const jwt = rootGetters['user/verifiedJWT']
    const method = payload.method || 'POST'

    // Update the payment options
    const paymentOptions = (!state.paymentOptions)
      ? {}
      : Object.entries(state.paymentOptions)
        .reduce((acc, [key, val]) => {
          if (typeof val === 'boolean') {
            return { ...acc, [key]: val ? 'true' : 'false' }
          }
          else if (Array.isArray(val)) {
            return { ...acc, [key]: val.join(',') }
          }
          else {
            return { ...acc, [key]: val }
          }
        }, {})

    const authorization = function() {
      return jwt ? `Bearer ${jwt}` : null
    }

    if (!Object.prototype.hasOwnProperty.call(payload, 'options')) {
      payload.options = { requestOptions: {} }
    }
    else if (!Object.prototype.hasOwnProperty.call(payload.options, 'requestOptions')) {
      payload.options.requestOptions = {}
    }
    return browserFingerprint()
      .then(fingerprint => {
        const reqOptions = {
          method: method,
          headers: filterHeaders({
            ...paymentOptions,
            ...payload.headers,
            'Content-Type': 'application/json',
            'X-Site-ID': rootGetters.getSiteId,
            'X-Browser-Fingerprint': fingerprint,
            Authorization: authorization(),
            Accept: 'application/json'
          }),
          body: ['POST', 'PUT', 'PATCH'].includes(payload.method) ? JSON.stringify(payload.body) : null
        }
        commit('PROCESSING_RESULT')
        commit('PROCESSING_REQUEST', {
          url: url,
          method: method,
          body: payload.body || null,
          headers: reqOptions.headers
        })
        return fetch(url, reqOptions)
      })
      .then(resp => {
        if (resp.ok) {
          return resp.json()
            .then(result => {
              commit('PROCESSING_RESULT', result)
              if (payload.options.requestOptions.debug) {
                console.log('Square Payments: API Call', {
                  Status: true,
                  Request: payload.body !== undefined ? payload.body : null,
                  Response: result
                })
              }

              // Handle both explicit errors and failed status responses
              if (result.error !== undefined || result.status === false) {
                // Safely handle errors array - check if it exists and is an array before calling shift()
                let err
                if (result.error && result.error.errors && Array.isArray(result.error.errors) && result.error.errors.length > 0) {
                  throw new SquarePaymentError(err.description || resp.statusText, {
                    code: resp.status,
                    reason: err.helpText || err.reason || err.detail,
                    category: result.error.errors.shift().code || 'UNKNOWN Error'
                  })
                }
                else if (result.status === false) {
                  // result.error may be undefined on auth/connected and other
                  // status:false responses that signal a non-payment failure
                  // shape (e.g. Square auth probe returning { status: false }
                  // with no error object). Reading result.error.code without
                  // a guard previously threw "Cannot read properties of
                  // undefined (reading 'code')" and propagated as an
                  // unhandled rejection — broke beforeEach hooks across the
                  // POS Stripe/Square sidebar tests. Guard each lookup so a
                  // missing error object surfaces as a soft SquarePaymentFailure
                  // with sensible defaults instead of crashing.
                  const errObj = result.error || {}
                  const message = errObj.message || result.message
                  return {
                    ...result,
                    error: new SquarePaymentFailure(message || 'Payment failed', {
                      code: errObj.code || resp.status,
                      reason: errObj.detail || 'Square API processing failed',
                      category: errObj.category || 'PAYMENT_FAILED',
                      response: result,
                      noStack: true
                    })
                  }
                }
                else if (result.error) {
                  // Handle cases where error object exists but errors array doesn't
                  throw new SquarePaymentError(result.error.message || resp.statusText, {
                    code: resp.status,
                    reason: result.error.detail || result.error.message || 'Unknown error',
                    category: result.error.category || result.error.code || 'UNKNOWN_ERROR'
                  })
                }
              }
              return result
            })
        }
        else {
          return resp.json()
            .then(result => {
              commit('PROCESSING_RESULT', result)
              if (payload.options.requestOptions.debug) {
                // console.log('Square Payments: API Call', {
                //   Status: false,
                //   Request: payload.body,
                //   Response: result
                // })
              }
              if (result.error === undefined) {
                throw new SquareServiceError(resp.statusText || result.reason, {
                  code: resp.status,
                  reason: result.reason,
                  category: result.category
                })
              }
              else {
                throw new SquarePaymentError(result.error.message, {
                  code: resp.status,
                  reason: result.error.message,
                  category: result.error.category
                })
              }
            })
        }
      })
  },
  getIsConnected: function({ state, commit, dispatch, getters, rootGetters }, payload) {
    const force = payload && payload.force !== undefined ? payload.force : false
    const cacheExpireTime = new Date().getTime() - state.isConnectedTimestamp

    if (getIsConnectedPromise) {
      return getIsConnectedPromise
    }

    if (getters.isConnected && !force) {
      return Promise.resolve(getters.isConnected)
    }
    // Any call to the getIsConnected method in the last 5 seconds will return the last status
    else if (cacheExpireTime < 5000) {
      if (getters.isConnected) {
        // console.debug('Returning cached Square Payments connection status')
        return Promise.resolve(getters.isConnected)
      }
    }

    let handler = getters.AcceleratedTransactionsHandler('getSquareIsConnected')
    if (handler) {
      // console.debug('Using Accelerated Transactions handler for getSquareIsConnected')
      handler = handler({ commit, dispatch, getters, rootGetters }, {
        payment_endpoint: state.endpoint,
        authorization: rootGetters['user/verifiedJWT'],
        force: force
      })
    }
    else {
      // console.debug('Using RestAPI handler for getSquareIsConnected')
      handler = dispatch('paymentAPICall', { method: 'GET', url: '/auth/connected' })
    }

    getIsConnectedPromise = handler.then(resp => {
      if (resp.status) {
        commit('SET_CONNECTED', resp.settings.expires)
        commit('SET_SITE_SETTINGS', {
          merchantid: resp.merchantid,
          ...resp.settings
        })
      }
      else {
        commit('SET_CONNECTED')
      }
      getIsConnectedPromise = null
      if (getters.isConnected) {
        return dispatch('loadLocations')
          .then(_ => getters.isConnected)
      }
      else {
        return getters.isConnected
      }
    })
      .catch(err => {
        commit('SET_CONNECTED')
        throw err
      })
    return getIsConnectedPromise
  },
  refreshauth: function({ state, commit, dispatch, getters }) {
    return dispatch('getIsConnected')
      .then(_ => {
        if (!getters.isConnected) {
          throw new Error('Not Connected')
        }
        return dispatch('paymentAPICall', {
          method: 'POST',
          url: '/oauth2/refresh'
        })
      })
      .then(resp => {
        if (resp.status) {
          commit('SET_CONNECTED', resp.expires)
        }
        else {
          commit('SET_CONNECTED')
        }
        return getters.isConnected
      })
      .catch(err => {
        commit('SET_CONNECTED')
        throw err
      })
  },
  revokeauth: function({ state, commit, dispatch, getters }) {
    return dispatch('getIsConnected')
      .then(_ => {
        if (!getters.isConnected) {
          throw new Error('Not Connected')
        }
        return dispatch('paymentAPICall', {
          method: 'POST',
          url: '/oauth2/revoke'
        })
      })
      .then(resp => {
        commit('SET_CONNECTED')
        return getters.isConnected
      })
  },
  initPayment: function({ state, commit, dispatch, getters, rootGetters }, payload) {
    if (!Object.prototype.hasOwnProperty.call(payload, 'txn')) {
      return Promise.reject(new SquarePaymentError('Txn is required to submit a payment', {
        category: 'USER-ERROR',
        detail: 'No Transaction details were provided to record against the payment.'
      }))
    }
    if (!isNumber(payload.amount) || parseFloat(payload.amount) <= 0) {
      return Promise.reject(new SquarePaymentError('Payment amount is required to submit a payment', {
        category: 'USER-ERROR',
        detail: 'No amount was provided to make the payment.'
      }))
    }

    commit('PAYMENT_TXN', payload.txn)
    const handler = getters.AcceleratedTransactionsHandler('initSquarePayment')
    if (handler) {
      return handler({}, {
        payment_endpoint: state.endpoint,
        authorization: rootGetters['user/verifiedJWT'],
        tempTxnID: payload.paymentOptions.txnData.tmpTxnid,
        txnid: payload.paymentOptions.txnData.txnid,
        txndate: payload.paymentOptions.txnData.txndate,
        total: payload.amount,
        request: {
          txnid: payload.paymentOptions.txnData.txnid,
          idempotency_key: generateUUID(),
          amount: payload.amount,
          userid: payload.userid.toString(),
          email: payload.paymentOptions.txnData.email,
          paymentOptions: payload.paymentOptions
        }
      })
    }
    else {
      return dispatch('paymentAPICall', {
        method: 'POST',
        url: '/card/payment/init',
        body: {
          txnid: payload.txn.txnid,
          amount: payload.amount,
          userid: (payload.userid !== undefined) ? payload.userid || '0' : rootGetters['user/userid'],
          paymentOptions: payload.paymentOptions
        },
        options: Object.prototype.hasOwnProperty.call(payload, 'options') ? payload.options : {}
      })
        .then(resp => {
          return resp
        })
        .catch(err => {
          console.error('Error initializing Square Payment', err)
          commit('PAYMENT_INIT_ERROR', err)
          throw err
        })
    }
  },
  initTerminalPayment: function({ state, commit, dispatch, getters, rootGetters }, payload) {
    if (!Object.prototype.hasOwnProperty.call(payload, 'txn')) {
      return Promise.reject(new SquarePaymentError('Txn is required to submit a payment', {
        category: 'USER-ERROR',
        detail: 'No Transaction details were provided to record against the payment.'
      }))
    }
    if (parseFloat(payload.amount) <= 0) {
      return Promise.reject(new SquarePaymentError('Payment amount is required to submit a payment', {
        category: 'USER-ERROR',
        detail: 'No amount was provided to make the payment.'
      }))
    }

    commit('PAYMENT_TXN', payload.txn)

    const handler = getters.AcceleratedTransactionsHandler('initSquareTerminalPayment')
    if (handler) {
      return handler({}, {
        payment_endpoint: state.endpoint,
        authorization: rootGetters['user/verifiedJWT'],
        tempTxnID: rootGetters.AcceleratedTransactionTmpTxnID,
        txnid: payload.txnid,
        txndate: rootGetters.getcreatedTransaction.txndate,
        total: rootGetters.getcreatedTransaction.total,
        request: {
          txnid: payload.txn.txnid,
          amount: payload.amount,
          userid: (payload.userid !== undefined) ? payload.userid || '0' : rootGetters['user/userid'],
          paymentOptions: payload.paymentOptions
        }
      })
    }
    else {
      return dispatch('paymentAPICall', {
        method: 'POST',
        url: '/terminal/payment/init',
        body: {
          txnid: payload.txn.txnid,
          amount: payload.amount,
          userid: (payload.userid !== undefined) ? payload.userid || '0' : rootGetters['user/userid'],
          paymentOptions: payload.paymentOptions
        },
        options: Object.prototype.hasOwnProperty.call(payload, 'options') ? payload.options : {}
      })
        .then(resp => {
          return resp
        })
        .catch(err => {
          console.error('Error initializing Square Payment', err)
          commit('PAYMENT_INIT_ERROR', err)
          throw err
        })
    }
  },
  storeCardDetails: async function({ state, dispatch, commit, rootGetters }, payload) {
    commit('PROCESSING_REQUEST')
    commit('PROCESSING_RESULT')

    if ((!payload.userid || payload.userid.trim().length === 0 || payload.userid.trim() === '0')) {
      return Promise.reject(new SquarePaymentError('A User Account is requred to store a Credit Card', {
        category: 'USER-ERROR',
        detail: 'You need to be logged in or specify a User Account to store the credit card against.',
        retryable: false
      }))
    }
    if (!payload.verificationDetails || !payload.verificationDetails.billingContact.givenName || payload.verificationDetails.billingContact.givenName.trim().length === 0) {
      return Promise.reject(new SquarePaymentError('Missing Customer firstname', {
        category: 'USER-ERROR',
        detail: 'Store Card must include the customer\'s first name.',
        retryable: false
      }))
    }
    if (!payload.verificationDetails || !payload.verificationDetails.billingContact.familyName || payload.verificationDetails.billingContact.familyName.trim().length === 0) {
      return Promise.reject(new SquarePaymentError('Missing Customer lastname', {
        category: 'USER-ERROR',
        detail: 'Store Card must include the customer\'s last name.',
        retryable: false
      }))
    }
    if (!payload.verificationDetails.billingContact.email || payload.verificationDetails.billingContact.email.trim().length === 0) {
      return Promise.reject(new SquarePaymentError('Missing customer email address', {
        category: 'USER-ERROR',
        detail: 'Store Card must include the customer\'s email address.',
        retryable: false
      }))
    }

    const userid = payload.userid !== undefined ? payload.userid : rootGetters['user/userid']
    let cardSource
    // Check that the transaction is not already completed
    if (payload.cardSource) {
      cardSource = payload.cardSource
    }
    else if (state.squareCard !== null) {
      // console.debug(`Verification Details:\n${JSON.stringify(payload.verificationDetails, null, 2)}`)
      cardSource = await dispatch('tokenizeCardDetails', payload.verificationDetails)
    }
    else {
      return Promise.reject(new SquarePaymentError('Card Details were not provided to make the payment', {
        category: 'USER-ERROR',
        detail: 'No Card Details were provided to make the payment.'
      }))
    }

    return dispatch('paymentAPICall', {
      method: 'POST',
      url: '/customer/payment-method',
      body: {
        storeCardToken: cardSource,
        customerDetails: payload.verificationDetails.billingContact,
        email: payload.email,
        userid: userid
      },
      headers: filterHeaders({
        'X-Recaptcha-Response': state.paymentVerification && Object.prototype.hasOwnProperty.call(state.paymentVerification, 'recaptch') ? state.paymentVerification.recaptch : null
      })
    })
      .then(resp => {
        // Format the response for the success modal system
        return {
          status: true,
          type: 'card',
          ...resp
        }
      })
  },
  processTerminalPayment: function({ state, dispatch, commit, rootGetters, getters }, payload) {
    commit('PROCESSING_REQUEST')
    commit('PROCESSING_RESULT')

    commit('TERMINAL_PAYMENT_PROCESSING', true)
    const userid = !this.isAnonymous ? payload.userid || state.paymentDetails.userid || '0' : null

    const handler = getters.AcceleratedTransactionsHandler('SquareCardPresentProcessPayment')
    const terminalDeviceID = getters.selectedTerminalDevice.device_id
    if (handler) {
      const processingRequest = {
        payment_endpoint: state.endpoint,
        authorization: rootGetters['user/verifiedJWT'],
        tempTxnID: rootGetters.AcceleratedTransactionTmpTxnID,
        txndate: rootGetters.getcreatedTransaction.txndate,
        total: rootGetters.getcreatedTransaction.total,
        txnid: state.paymentDetails.txnid,
        source: 'pos',
        userid: rootGetters['user/userid'],
        request: {
          paymentRequestID: payload.paymentRequestID,
          device_id: terminalDeviceID,
          userid: userid,
          storeCard: payload.userid !== '0' && payload.storeCard !== undefined ? payload.storeCard : false
        },
        // Extended retry configuration for terminal payments
        extendedRetry: true,
        onTimeout: payload.onTimeout,
        onRetryAttempt: payload.onRetryAttempt,
        getCancelFunction: payload.getCancelFunction,
        timeoutDuration: payload.timeoutDuration || 30000 // Use provided duration or default to 30 seconds
      }
      commit('PROCESSING_REQUEST', processingRequest)
      return handler({}, processingRequest)
        .then(resp => {
          if (Object.prototype.hasOwnProperty.call(resp, 'paymentResponse')) {
            commit('PROCESSING_RESULT', resp.paymentResponse)
            return resp.paymentResponse
          }
          else {
            console.error('Square Card Not Present Process Payment did not return a paymentResponse', resp)
            throw new SquarePaymentError('Square Card Not Present Process Payment did not return a paymentResponse')
          }
        })
        .finally(() => {
          commit('TERMINAL_PAYMENT_PROCESSING', false)
        })
    }
    else {
      return dispatch('paymentAPICall', {
        method: 'POST',
        url: '/terminal/payment/process',
        body: {
          paymentRequestID: payload.paymentRequestID,
          device_id: terminalDeviceID,
          userid: userid,
          storeCard: payload.userid !== '0' && payload.storeCard !== undefined ? payload.storeCard : false
        },
        options: Object.prototype.hasOwnProperty.call(payload, 'options') ? payload.options : {}
      })
        .then(response => {
          // Ensure terminal payment responses have the correct source_type
          if (response && response.payment && !response.payment.source_type) {
            response.payment.source_type = 'TERMINAL'
          }
          return response
        })
        .finally(() => {
          commit('TERMINAL_PAYMENT_PROCESSING', false)
        })
    }
  },
  cancelTerminalPaymentRequest: function({ getters, dispatch, commit }, payload) {
    return dispatch('paymentAPICall', {
      method: 'DELETE',
      url: `/terminal/payment/cancel/${getters.selectedTerminalDevice.device_id}`
    })
      .then(response => {
        commit('TERMINAL_PAYMENT_PROCESSING', false)
        return response
      })
  },
  processPayment: async function({ state, getters, rootGetters, dispatch, commit }, payload) {
    commit('PROCESSING_REQUEST')
    commit('PROCESSING_RESULT')
    let cardSource
    // Check that the transaction is not already completed
    if (payload.cardSource) {
      cardSource = payload.cardSource
    }
    else if (state.squareCard !== null) {
      // console.debug(`Verification Details:\n${JSON.stringify(payload.verificationDetails, null, 2)}`)
      cardSource = await dispatch('tokenizeCardDetails', payload.verificationDetails)
    }
    else {
      return Promise.reject(new SquarePaymentError('Card Details were not provided to make the payment', {
        category: 'USER-ERROR',
        detail: 'No Card Details were provided to make the payment.'
      }))
    }
    const userid = !this.isAnonymous ? payload.userid || state.paymentDetails.userid || '0' : null

    const handler = getters.AcceleratedTransactionsHandler('SquareCardNotPresentProcessPayment')
    if (handler) {
      const processingRequest = {
        payment_endpoint: state.endpoint,
        authorization: rootGetters['user/verifiedJWT'],
        tempTxnID: rootGetters.AcceleratedTransactionTmpTxnID,
        txndate: rootGetters.getcreatedTransaction.txndate,
        total: rootGetters.getcreatedTransaction.total,
        txnid: state.paymentDetails.txnid,
        source: 'pos',
        userid: rootGetters['user/userid'],
        request: {
          paymentRequestID: state.paymentDetails.id,
          cardtoken: cardSource,
          userid: userid,
          storeCard: userid !== '0' && payload.storeCard !== undefined ? payload.storeCard : false
        }
      }
      commit('PROCESSING_REQUEST', processingRequest)
      return handler({}, processingRequest)
        .then(resp => {
          if (Object.prototype.hasOwnProperty.call(resp, 'paymentResponse')) {
            commit('PROCESSING_RESULT', resp.paymentResponse)
            return resp.paymentResponse
          }
          else {
            console.error('Square Card Not Present Process Payment did not return a paymentResponse', resp)
            throw new SquarePaymentError('Square Card Not Present Process Payment did not return a paymentResponse')
          }
        })
    }
    else {
      return dispatch('paymentAPICall', {
        method: 'POST',
        url: '/card/payment/process',
        body: {
          paymentRequestID: state.paymentDetails.id,
          cardtoken: cardSource,
          userid: userid,
          storeCard: userid !== '0' && payload.storeCard !== undefined ? payload.storeCard : false
        },
        headers: {
          'X-Recaptcha-Response': payload.recaptch
        },
        options: Object.prototype.hasOwnProperty.call(payload, 'options') ? payload.options : {}
      })
    //   return Promise.resolve({
    //     status: false,
    //     payment: {
    //       id: 'NqABxS23apsmaIRSCC72o9qSJ6FZY',
    //       created_at: '2025-09-14T22:34:30.173Z',
    //       updated_at: '2025-09-14T22:34:31.187Z',
    //       amount_money: {
    //         amount: 200,
    //         currency: 'AUD'
    //       },
    //       app_fee_money: {
    //         amount: 2,
    //         currency: 'AUD'
    //       },
    //       status: 'FAILED',
    //       delay_duration: 'PT168H',
    //       source_type: 'CARD',
    //       card_details: {
    //         status: 'FAILED',
    //         card: {
    //           card_brand: 'MASTERCARD',
    //           last_4: '4481',
    //           exp_month: 6,
    //           exp_year: 2029,
    //           fingerprint: 'sq-1-7nM2cNYYUPAT1lS6Xfcjrwdg__mFm9v9lYM01aoZ3CHDHIfLaebL3o1c1B8CWvwlyA',
    //           card_type: 'DEBIT',
    //           prepaid_type: 'PREPAID',
    //           bin: '538660',
    //           payment_account_reference: '5001CBSX7TDCM54RJW7GTUMBMF4OZ'
    //         },
    //         entry_method: 'KEYED',
    //         cvv_status: 'CVV_ACCEPTED',
    //         avs_status: 'AVS_NOT_CHECKED',
    //         errors: [
    //           {
    //             code: 'GENERIC_DECLINE',
    //             detail: "Authorization error: 'GENERIC_DECLINE'",
    //             category: 'PAYMENT_METHOD_ERROR'
    //           }
    //         ],
    //         statement_description: 'SQ *SENTRINO PTY LTD',
    //         card_payment_timeline: {
    //           authorized_at: '2025-09-14T22:34:31.187Z'
    //         }
    //       },
    //       location_id: 'LPY5HSM4TPNBJ',
    //       order_id: 'TkXhFrenIk8UmUGa9FX0T33oRd6YY',
    //       reference_id: 'eb28271d-3c9f-42ab-b9d3-3b84d49d633b',
    //       total_money: {
    //         amount: 200,
    //         currency: 'AUD'
    //       },
    //       approved_money: {
    //         amount: 0,
    //         currency: 'AUD'
    //       },
    //       delay_action: 'CANCEL',
    //       delayed_until: '2025-09-21T22:34:30.173Z',
    //       application_details: {
    //         square_product: 'ECOMMERCE_API',
    //         application_id: 'sq0idp-o7upiNKgVVNq6eblil9EBA'
    //       },
    //       version_token: 'rBrIdXfwYMVfU1uGSWVoEC5I2yBhYN0Ou8xziYVgFzu6o'
    //     },
    //     error: {
    //       code: 'GENERIC_DECLINE',
    //       detail: "Authorization error: 'GENERIC_DECLINE'",
    //       category: 'PAYMENT_METHOD_ERROR',
    //       message: "We couldn't process your payment: Authorization error: 'GENERIC_DECLINE'. Please try again or contact support for assistance."
    //     }
    //   })
    }
  },
  loadCurrencies: function({ state, commit, dispatch }) {
    if (!Object.keys(state.currencies).length) {
      return dispatch('paymentAPICall', { method: 'GET', url: '/settings/currencies' })
        .then(resp => {
          commit('SET_CURRENCIES', resp.available)
          commit('SET_DEFAULT_CURRENCY', resp.default)
        })
    }
  },
  revokeAuthToken: function({ state, commit, dispatch }) {
    return dispatch('paymentAPICall', { method: 'DELETE', url: '/auth' })
      .then(resp => {
        commit('SET_CONNECTED')
        dispatch('getIsConnected', { force: true })
        return resp
      })
  },
  refreshAuthToken: function({ dispatch, commit }) {
    return dispatch('paymentAPICall', { method: 'PATCH', url: '/auth' })
      .then(resp => {
        commit('SET_CONNECTED', resp.settings.expires)
        dispatch('getIsConnected', { force: true })
        return resp
      })
  },
  loadCustomerCards: function({ state, commit, dispatch, getters }, payload) {
    return dispatch('getIsConnected')
      .catch(_err => {
        return Promise.reject(new SquarePaymentError('Not connected to Square Payments', {
          category: 'USER-ERROR',
          detail: 'You must be connected to Square Payments to load customer cards.'
        }))
      })
      .then(_ => {
        const userid = payload.userid || payload.user_id
        return dispatch('paymentAPICall', {
          method: 'GET',
          url: `/customer/${userid}/payment-methods`
        })
      })
      .then(resp => {
        return resp.cards || []
      })
  },
  removeStoredCard: function({ dispatch }, payload) {
    return dispatch('getIsConnected')
      .catch(_err => {
        return Promise.reject(new SquarePaymentError('Not connected to Square Payments', {
          category: 'USER-ERROR',
          detail: 'You must be connected to Square Payments to remove a stored card.'
        }))
      })
      .then(_ => dispatch('paymentAPICall', {
        method: 'DELETE',
        url: `/customer/${payload.userid}/payment-method/${payload.cardid}`,
        options: Object.prototype.hasOwnProperty.call(payload, 'options') ? payload.options : {}
      }))
      .then(resp => {
        return resp
      })
  },
  loadLocations: function({ state, commit, dispatch }, payload) {
    const shouldForceReload = payload && typeof payload.force === 'boolean' && payload.force

    if (shouldForceReload || !state.locations.length) {
      return dispatch('paymentAPICall', { method: 'GET', url: '/locations' })
        .then(resp => {
          // Map locations from API format to internal format
          const mappedLocations = resp.locations.map(location => mapLocationFromAPI(location))
          commit('LOCATIONS', mappedLocations)

          // Check if current location is valid, if not set to first available location
          if (mappedLocations.length > 0) {
            const currentLocationValid = state.location && mappedLocations.some(loc => loc.id === state.location)
            if (!currentLocationValid) {
              commit('SET_LOCATION', mappedLocations[0].id)
            }
          }

          return mappedLocations
        })
    }
    return Promise.resolve(state.locations)
  },
  activateLocation: function({ dispatch }, payload) {
    return dispatch('paymentAPICall', {
      url: `/locations/${payload}/activate`,
      method: 'PATCH'
    })
      .then(resp => dispatch('loadLocations', { force: true }))
  },
  deactivateLocation: function({ dispatch }, payload) {
    return dispatch('paymentAPICall', {
      url: `/locations/${payload}/deactivate`,
      method: 'PATCH'
    })
      .then(resp => {
        if (resp.status) {
          return dispatch('loadLocations', { force: true })
            .then(_ => resp)
        }
        else {
          return resp
        }
      })
      .catch(err => {
        console.error(`Error deactivating location: ${err.message}`)
        return {
          status: false,
          error: {
            code: err.code || 'UNKNOWN_ERROR',
            message: err.reason.message ||
              err.reason.detail ||
              err.message ||
              'An unknown error occurred while deactivating the location.'
          }
        }
      })
  },
  createLocation: function({ dispatch }, payload) {
    // Map form data to API format
    const apiPayload = mapLocationToAPI(payload)

    return dispatch('paymentAPICall', {
      url: '/locations',
      method: 'POST',
      body: apiPayload
    })
      .then(resp => {
        // Reload locations in the background but return the created location
        dispatch('loadLocations', { force: true })
        // Return the location data that was created, mapped to internal format
        return resp.location ? mapLocationFromAPI(resp.location) : { name: payload.name, ...resp }
      })
  },
  updateLocation: function({ dispatch }, payload) {
    // Extract locationId and map the rest to API format
    const { locationId, ...formData } = payload
    const apiPayload = mapLocationToAPI(formData)

    return dispatch('paymentAPICall', {
      url: `/locations/${locationId}`,
      method: 'PATCH',
      body: apiPayload
    })
      .then(resp => {
        // Reload locations in the background but return the updated location
        dispatch('loadLocations', { force: true })
        // Return the location data that was updated, mapped to internal form
        return resp.location ? mapLocationFromAPI(resp.location) : { name: formData.name, ...resp }
      })
  },
  loadLocationsByTxnType: function({ commit, dispatch }) {
    // The payment service returns both the current mapping AND the available
    // transaction types for the site on this one route, so the component does
    // not need a separate datastore/drupal call.
    return dispatch('paymentAPICall', { method: 'GET', url: '/config/locations-by-txntype' })
      .then(resp => {
        const rawMap = unwrapEnvelope(resp && resp.location_by_txntype)
        const map = (rawMap && typeof rawMap === 'object' && !Array.isArray(rawMap)) ? rawMap : {}
        const types = normalizeTransactionTypes(resp && resp.transaction_types)
        commit('SET_LOCATIONS_BY_TXNTYPE', map)
        commit('SET_TRANSACTION_TYPES', types)
        return { map, types }
      })
  },
  saveLocationsByTxnType: function({ commit, dispatch }, payload) {
    // payload.map is the full, already-diffed mapping. Txntypes routing to the
    // Square default are absent from the map (never persisted as empty string).
    const map = (payload && payload.map) || {}
    return dispatch('paymentAPICall', {
      method: 'PUT',
      url: '/config/locations-by-txntype',
      body: { location_by_txntype: map }
    })
      .then(resp => {
        // PUT replaces the whole map; trust the request copy as the new truth,
        // falling back to whatever the service echoes back.
        const saved = (resp && resp.location_by_txntype) || map
        commit('SET_LOCATIONS_BY_TXNTYPE', saved)
        return saved
      })
  },
  squareCard: function({ commit, getters }, payload) {
    return window.Square.payments(getters.appID, getters.locationID).card()
      .then(card => {
        commit('SET_SQUARE_CARD', card)
        card.attach(payload.elementId)
      })
  },
  destroySquareCard: function({ commit, state }) {
    // Destroy existing Square Card instance if it exists
    try {
      if (state.squareCard) {
        state.squareCard.destroy()
      }
      commit('SET_SQUARE_CARD')
    }
    catch (err) {
      // Ignore errors during cleanup
      console.warn('Error destroying Square Card:', err)
    }
  },
  tokenizeCardDetails: async function({ commit, state, getters }, verificationDetails) {
    try {
      if (state.squareCard !== null) {
        const tokenizeResult = await state.squareCard.tokenize(verificationDetails)
        if (tokenizeResult.status === 'OK') {
          return tokenizeResult.token
        }
        else {
          const errors = tokenizeResult.errors.map(error => `${error.field} ${error.message}`).join(', ')
          throw new SquareCardTokenizationError(errors, {
            tokenizeResult: tokenizeResult,
            category: 'CARD-TOKENIZATION',
            details: 'There was an error tokenizing the Credit/Debit Card Details.',
            reason: errors || 'Unknown Error',
            retryable: true
          })
        }
      }
    }
    catch (err) {
      throw new SquareCardTokenizationError('Card Tokenization Error', {
        category: 'CARD-TOKENIZATION',
        details: 'There was an error tokenizing the Credit/Debit Card Details.',
        reason: err.message || 'Unknown Error',
        error: err,
        retryable: false
      })
    }
  },
  loadDevicesList: function({ state, commit, dispatch }, payload) {
    const shouldForceReload = payload && typeof payload.force === 'boolean' && payload.force

    if (shouldForceReload || !state.terminalDevices.length) {
      return dispatch('paymentAPICall', { method: 'GET', url: '/terminal/list' })
        .then(resp => {
          if (resp.status) {
            commit('SET_TERMINAL_DEVICES', resp.devices)
            return resp.devices
          }
          else {
            throw new SquareServiceError('Failed to load terminal devices', {
              code: resp.code || 'UNKNOWN_ERROR',
              reason: resp.reason || 'An unknown error occurred while loading terminal devices.'
            })
          }
        })
    }
    return Promise.resolve(state.terminalDevices)
  },
  loadDeviceCodesList: function({ state, commit, dispatch, getters }, payload) {
    const shouldForceReload = payload && typeof payload.force === 'boolean' && payload.force

    if (getters.testMode) {
      // When in test mode, return static test data for terminal payments testing
      // BUT skip this behavior when Cypress is running on the device codes list page
      // to allow proper API mocking for device codes management tests
      // Check if we're running in Cypress test environment AND on the device codes list page
      const isCypressRunning = typeof window !== 'undefined' && window.Cypress
      const isDeviceCodesListPage = typeof window !== 'undefined' &&
        window.location &&
        window.location.pathname === '/devices/codes'
      if (!(isCypressRunning && isDeviceCodesListPage)) {
        commit('SET_TERMINAL_DEVICE_CODES', testDeviceCodes)
        return Promise.resolve(testDeviceCodes)
      }
    }

    if (shouldForceReload || !state.terminalDeviceCodes.length) {
      return dispatch('paymentAPICall', { method: 'GET', url: '/terminal/codes/list' })
        .then(resp => {
          if (resp.status) {
            commit('SET_TERMINAL_DEVICE_CODES', resp.deviceCodes)
            return resp.deviceCodes
          }
          else {
            throw new SquareServiceError('Failed to load terminal device codes', {
              code: resp.code || 'UNKNOWN_ERROR',
              reason: resp.reason || 'An unknown error occurred while loading terminal device codes.'
            })
          }
        })
    }
    return Promise.resolve(state.terminalDeviceCodes)
  },
  createDeviceCode: function({ state, dispatch }, payload) {
    // Validate that locationId is provided
    if (!payload.locationId || payload.locationId.trim().length === 0) {
      return Promise.reject(new SquareServiceError('Location ID is required to create device code', {
        code: 'INVALID_LOCATION_ID',
        reason: 'A valid location ID must be provided to create a device code.'
      }))
    }

    // Ensure locations are loaded for validation
    const ensureLocationsLoaded = state.locations.length === 0
      ? dispatch('loadLocations')
      : Promise.resolve(state.locations)

    return ensureLocationsLoaded
      .then(locations => {
        // Validate that the locationId exists in the loaded locations
        const locationExists = locations.some(location => location.id === payload.locationId)

        if (!locationExists) {
          throw new SquareServiceError('Invalid Location ID', {
            code: 'LOCATION_NOT_FOUND',
            reason: `Location ID '${payload.locationId}' does not exist in the available locations.`
          })
        }

        // Proceed with device code creation if validation passes
        return dispatch('paymentAPICall', {
          method: 'POST',
          url: '/terminal/create',
          body: {
            location_id: payload.locationId,
            device_name: payload.deviceName || 'Square Terminal'
          }
        })
      })
      .then(resp => {
        // trigger a reload of devices list to reflect changes
        return dispatch('loadDevicesList', { force: true })
          .then(() => {
            // Return the device code created
            return resp
          })
      })
      .catch(err => {
        console.error('Error creating device code:', err)

        // Re-throw SquareServiceError instances (our validation errors) as-is
        if (err instanceof SquareServiceError) {
          throw err
        }

        // Wrap other errors in SquareServiceError
        throw new SquareServiceError('Failed to create device code', {
          code: err.code || 'UNKNOWN_ERROR',
          reason: err.message || 'An unknown error occurred while creating the device code.'
        })
      })
  },
  removeDeviceCode: function({ state, dispatch }, payload) {
    // Validate that deviceCode is provided
    if (!payload.deviceCode || payload.deviceCode.trim().length === 0) {
      return Promise.reject(new SquareServiceError('Device Code is required to remove a device code', {
        code: 'INVALID_DEVICE_CODE',
        reason: 'A valid device code must be provided to remove a device code.'
      }))
    }

    return dispatch('paymentAPICall', {
      method: 'DELETE',
      url: `/terminal/code/${payload.deviceCode}`
    })
      .then(resp => {
        // Return the response without automatically reloading the list
        // The component will handle the list refresh after setting the success message
        return resp
      })
  },
  registerAcceleratedTransactionHandlers: function({ commit }, payload) {
    commit('REGISTER_ACCELERATED_TXN_HANDLERS', payload)
  }

}
